import type { ComponentProps, ReactNode } from 'react'
import cn from 'classnames'
import { TFunction } from 'next-i18next/pages'
import { ProductPrice } from '@components/common/ProductPrice/ProductPrice'
import { GiftCardProduct } from '@corratech/pylot-product-giftcard'
import GroupedProduct from '@components/common/GroupedProduct'
import BundleProducts from '@components/BundleProducts/BundleProducts'
import { VentiPayWidget } from '@lib/utils/venti-widget'
import { VariantSelector } from '@corratech/pylot-variant-selector'
import { AssociatedProductThumbs } from '@components/common/AssociatedProducts/AssociatedProductThumbs'
import { useAssociatedProductsTitle } from '@lib/hooks/product/associated-products/useAssociatedProductsConfig'
import {
    ConfigurableProduct,
    ConfigurableVariant
} from '@pylot-data/pylotschema'
import { ProductStockStatus } from '@pylot-data/enums/ProductStockStatus'
import { QuantitySelector } from '@components/QuantitySelector/QuantitySelector'
import { Accordion, Button } from '@corratech/pylot-ui'
import { AddToWishlist } from '@corratech/pylot-wishlist'
import SuggestedSet from '@components/common/SuggestedSet'
import { IconClose, IconOpen } from '@components/icons'
import s from '@pagestyles/PDP.module.scss'

/**
 * EVO-263: la fila de colores incluye al producto que se está viendo, para que su color quede
 * preseleccionado igual que en la PLP y el SRP. `associated_products` nunca se lista a sí mismo,
 * así que hay que anteponerlo; el componente lo marca con `aria-current` comparando `currentSku`.
 *
 * Si el producto no tiene hermanos se devuelve la lista tal cual —vacía o nula— para que el
 * bloque no se pinte con una sola miniatura, que no comunicaría nada.
 */
const buildColorGroup = (product: any): any => {
    const siblings = product?.associated_products

    if (!Array.isArray(siblings) || !siblings.length) {
        return siblings
    }

    return [product, ...siblings]
}

type PDPMainColumnProps = {
    t: TFunction
    sku?: string
    hasQrCode: boolean
    name?: string
    isGiftCard: boolean
    isGrouped: boolean
    isBundle: boolean
    isConfig: boolean
    product: any
    variantPriceRange: any
    productPriceRange: any
    pdpData: any
    badgeCaptionIcon?: string
    ventiFinalPrice: number
    options: any
    selectedOptions: any
    setSelectedOptions: (value: any) => void
    quantity: number
    setQuantity: (value: any) => void
    buttonLabelWithoutAdding: string
    addProductHandler: () => void
    isAddingToCart: boolean
    isAdding: boolean
    disabledAddToCart: boolean
    isOutOfStockByQty: boolean
    buttonLabel: string
    selectedOptionUIDs: string[]
    isConfigOptionSelected: boolean
    wishlistButtonContent: any
    reviewStars: ReactNode
    onOpenQrCode: () => void
    onOpenStoreStock: () => void
    isSuggestedSet: boolean
    mainProductForSet?: any
    mainProductCartForSet?: any
    accordionContents: ComponentProps<typeof Accordion>['contents']
}

export function PDPMainColumn({
    t,
    sku,
    hasQrCode,
    name,
    isGiftCard,
    isGrouped,
    isBundle,
    isConfig,
    product,
    variantPriceRange,
    productPriceRange,
    pdpData,
    badgeCaptionIcon,
    ventiFinalPrice,
    options,
    selectedOptions,
    setSelectedOptions,
    quantity,
    setQuantity,
    buttonLabelWithoutAdding,
    addProductHandler,
    isAddingToCart,
    isAdding,
    disabledAddToCart,
    isOutOfStockByQty,
    buttonLabel,
    selectedOptionUIDs,
    isConfigOptionSelected,
    wishlistButtonContent,
    reviewStars,
    onOpenQrCode,
    onOpenStoreStock,
    isSuggestedSet,
    mainProductForSet,
    mainProductCartForSet,
    accordionContents
}: PDPMainColumnProps) {
    /**
     * EVO-263: el titulo del bloque de colores es configurable.
     * Cascada: atributo del producto (`associated_products_title`, scope store
     * view) -> default del storeConfig (`associated_products_default_title`) ->
     * texto traducido del storefront.
     */
    const associatedProductsTitle = useAssociatedProductsTitle(
        product?.associated_products_title,
        t('pdp||Available colors')
    )

    return (
        <div className={s['product-main']}>
            <div className={s['product-details']}>
                <div className={s['product-topinfo']}>
                    {!isGrouped && (
                        <span className={s['sku']} aria-label={sku}>
                            {t('SKU: ')}
                            {sku}
                        </span>
                    )}

                    {hasQrCode ? (
                        <>
                            <span className={cn(s['separator'], s['sku'])}>
                                |
                            </span>
                            <Button
                                variant="link"
                                className={s['qr-code-button']}
                                onClick={onOpenQrCode}
                            >
                                <span className={s['sku']}>
                                    {t('pdp||See QR Code')}
                                </span>
                            </Button>
                        </>
                    ) : null}
                </div>

                <div>
                    <h1 className={cn(s['product-name'], 'heading-4')}>
                        {name}
                    </h1>
                    {reviewStars}
                </div>

                {isGiftCard ? (
                    <GiftCardProduct product={product} />
                ) : isGrouped ? (
                    <GroupedProduct
                        products={(product?.items ?? [])
                            .map((it: any) => it?.product)
                            .filter(Boolean)}
                        groupedPriceRange={product?.price_range}
                        descriptionHtml={pdpData?.short_description?.html}
                        wishlist={
                            <div className={s['wishlist-btn-container']}>
                                <AddToWishlist
                                    stayOnPage
                                    sku={product?.sku}
                                    selected_options={[]}
                                    quantity={1}
                                    isWishlistDisabled={false}
                                    isConfig={false}
                                    enableRemoveProductFromWishlist
                                    buttonContent={wishlistButtonContent}
                                />
                            </div>
                        }
                    />
                ) : (
                    <>
                        <div className={s['pdp-price']}>
                            <ProductPrice
                                className={s['pdp-product-price']}
                                priceRange={
                                    variantPriceRange || productPriceRange
                                }
                            />
                            {pdpData?.effectiveIsSet === 1 &&
                            typeof pdpData?.effectivePpum === 'number' &&
                            !isNaN(pdpData?.effectivePpum) ? (
                                <div className={s['price-per-unit-cont']}>
                                    <span>
                                        {t('pdp||Price per unit')}{' '}
                                        {pdpData?.unitPrice}
                                    </span>
                                </div>
                            ) : null}
                        </div>

                        {isBundle && <BundleProducts items={product?.items} />}

                        {badgeCaptionIcon && (
                            <div
                                className={s['pdp-badge-price']}
                                dangerouslySetInnerHTML={{
                                    __html: badgeCaptionIcon
                                }}
                            />
                        )}

                        <div className="flex items-center">
                            <VentiPayWidget
                                productId={product.id}
                                finalPrice={ventiFinalPrice}
                            />
                        </div>

                        {/*
                            EVO-263: bloque "Colores Disponibles" entre el
                            widget de cuotas y el selector de talla. Cada color
                            es un configurable aparte
                            (`associated_products`), y cada miniatura con stock
                            navega client-side a la PDP de ese color: foto,
                            precio y stock de tallas se actualizan sin recargar
                            la pagina. El tope visible sale de
                            `associated_products_pdp_limit`, y la fila incluye
                            al producto actual, marcado como seleccionado.
                        */}
                        <AssociatedProductThumbs
                            products={buildColorGroup(product)}
                            currentSku={product?.sku}
                            title={associatedProductsTitle}
                            context="pdp"
                            className="pb-4 md:pb-2"
                        />

                        {isConfig && product.variants ? (
                            <div className="pb-4 md:pb-2">
                                <VariantSelector
                                    disableAll={
                                        (product as ConfigurableProduct)
                                            .stock_status ===
                                        ProductStockStatus.OutOfStock
                                    }
                                    // EVO-245: en la ficha se muestran tambien las
                                    // tallas sin stock, tachadas y no seleccionables.
                                    showUnavailableOptions
                                    variants={
                                        product.variants as ConfigurableVariant[]
                                    }
                                    options={options}
                                    selectedOptions={selectedOptions}
                                    setSelectedOptions={setSelectedOptions}
                                />
                            </div>
                        ) : null}

                        <div className={s['qty-selector']}>
                            {isBundle && (
                                <div className={s['bundle-qty']}>
                                    {t('pdp||How many sets')}
                                </div>
                            )}
                            <p className={s['title']}>{t('pdp||QTY:')}</p>
                            <div className={s['buttons-container']}>
                                <div>
                                    <div className={s['quantity-selector']}>
                                        <QuantitySelector
                                            quantity={quantity}
                                            setQuantity={setQuantity}
                                        />
                                    </div>
                                </div>

                                <Button
                                    aria-label={t('Add to Cart')}
                                    type="button"
                                    className={cn(
                                        'btn-add-to-cart-plp-pdp',
                                        s['add-to-cart-button'],
                                        buttonLabelWithoutAdding ===
                                            t('Select a variant')
                                            ? s['select-variant']
                                            : ''
                                    )}
                                    onClick={addProductHandler}
                                    loading={isAddingToCart || isAdding}
                                    disabled={disabledAddToCart}
                                >
                                    {isOutOfStockByQty
                                        ? t('Stock insuficiente')
                                        : buttonLabel}
                                </Button>
                            </div>
                        </div>

                        <div className={s['wishlist-btn-container']}>
                            <AddToWishlist
                                stayOnPage
                                sku={product?.sku}
                                selected_options={selectedOptionUIDs}
                                quantity={1}
                                isWishlistDisabled={isConfigOptionSelected}
                                isConfig={isConfig}
                                enableRemoveProductFromWishlist
                                buttonContent={wishlistButtonContent}
                            />
                        </div>

                        <div className="flex items-center mb-3 md:mb-3.5">
                            <div className={s['icon-circle']}>
                                <img
                                    src="/icons/store.svg"
                                    alt={t('store')}
                                    width={17}
                                    height={16}
                                />
                            </div>
                            <Button variant="link" onClick={onOpenStoreStock}>
                                <span className={s['store-stock-title']}>
                                    {t('Ver stock en tiendas')}
                                </span>
                            </Button>
                        </div>

                        {isSuggestedSet ? (
                            <div className="flex items-center">
                                <SuggestedSet
                                    mainProduct={mainProductForSet}
                                    mainProductCart={mainProductCartForSet}
                                    products={product?.related_products}
                                />
                            </div>
                        ) : null}
                    </>
                )}

                {isGrouped && (
                    <div className="flex items-center mb-2 md:mb-3">
                        <VentiPayWidget
                            productId={product.id}
                            finalPrice={ventiFinalPrice}
                        />
                    </div>
                )}

                <Accordion
                    className="border-t border-color-s-4"
                    OpenIcon={<IconOpen className="ml-auto mr-6" />}
                    CloseIcon={<IconClose className="ml-auto mr-6" />}
                    contents={accordionContents}
                />
            </div>
        </div>
    )
}
